/**
 * Policy management rules shared by the admin client and the Pages Functions API.
 *
 * The mobile app (wellness_app, Dart) hashes policy content with
 *   sha256.convert(utf8.encode(contentMarkdown.trim()))
 * Dart's String.trim() strips a different whitespace set from JavaScript's
 * String.prototype.trim() (it also strips U+0085 NEXT LINE), so content is
 * canonicalised with a Dart-equivalent trim before it is stored and hashed.
 * Because the stored text is already canonical, re-hashing it in Dart gives the
 * same digest.
 */

export const POLICY_SCOPES = ['global', 'feature_group', 'feature_key'];
export const BUMP_TYPES = ['major', 'minor', 'patch'];
export const KNOWN_POLICY_TYPES = [
  'privacy_policy',
  'app_terms',
  'website_terms',
  'healthkey_data_sharing',
];

// Code units Dart's String.trim() treats as whitespace. Verified exhaustively
// against the Dart SDK over U+0000–U+FFFF.
const DART_WHITESPACE =
  '\\t\\n\\v\\f\\r \\u0085\\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000\\uFEFF';
const DART_LEADING_WHITESPACE = new RegExp(`^[${DART_WHITESPACE}]+`);
const DART_TRAILING_WHITESPACE = new RegExp(`[${DART_WHITESPACE}]+$`);

/** Trim exactly the characters Dart's String.trim() removes. */
export function dartTrim(text) {
  return String(text).replace(DART_LEADING_WHITESPACE, '').replace(DART_TRAILING_WHITESPACE, '');
}

/** Normalise line endings to LF and apply the Dart-equivalent trim. */
export function canonicalizeMarkdown(text) {
  return dartTrim(String(text ?? '').replace(/\r\n?/g, '\n'));
}

/** Lowercase hex SHA-256 of the UTF-8 encoding of `text`. */
export async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
}

/** The content hash the mobile app computes for a policy version. */
export function policyContentHash(contentMarkdown) {
  return sha256Hex(canonicalizeMarkdown(contentMarkdown));
}

// ── Semantic versions ────────────────────────────────────────────────────────

const SEMVER_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

/** Parse "MAJOR.MINOR.PATCH" (no pre-release or build metadata). */
export function parseSemver(version) {
  const match = SEMVER_PATTERN.exec(String(version ?? '').trim());
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]), patch: Number(match[3]) };
}

export function formatSemver({ major, minor, patch }) {
  return `${major}.${minor}.${patch}`;
}

/** Negative, zero or positive, like a sort comparator. Both must be valid. */
export function compareSemver(a, b) {
  const x = typeof a === 'string' ? parseSemver(a) : a;
  const y = typeof b === 'string' ? parseSemver(b) : b;
  if (!x || !y) throw new Error('compareSemver needs two valid versions');
  return x.major - y.major || x.minor - y.minor || x.patch - y.patch;
}

/** The version a bump produces. A policy with no published version starts at 1.0.0. */
export function nextVersion(currentVersion, bump) {
  const current = currentVersion ? parseSemver(currentVersion) : null;
  if (!current) return '1.0.0';
  if (bump === 'major') return formatSemver({ major: current.major + 1, minor: 0, patch: 0 });
  if (bump === 'minor') return formatSemver({ major: current.major, minor: current.minor + 1, patch: 0 });
  return formatSemver({ major: current.major, minor: current.minor, patch: current.patch + 1 });
}

/** 'initial' | 'major' | 'minor' | 'patch' for a move from `previous` to `next`. */
export function bumpTypeOf(previousVersion, version) {
  const previous = previousVersion ? parseSemver(previousVersion) : null;
  const next = parseSemver(version);
  if (!next) return null;
  if (!previous) return 'initial';
  if (next.major !== previous.major) return 'major';
  if (next.minor !== previous.minor) return 'minor';
  return 'patch';
}

// ── Policy identity and scope ────────────────────────────────────────────────

const POLICY_ID_PATTERN = /^[A-Za-z0-9_-]{1,100}$/;

export function isValidPolicyId(id) {
  return typeof id === 'string' && POLICY_ID_PATTERN.test(id);
}

/**
 * Suggest a document ID following the mobile app's direct-lookup conventions
 * (PolicyService.getFeatureGroupPolicy / getFeatureKeyPolicy).
 */
export function suggestPolicyId(scope, { title = '', featureGroupId = '', featureKeyId = '' } = {}) {
  if (scope === 'feature_group') return featureGroupId ? `group_${featureGroupId}` : '';
  if (scope === 'feature_key') return featureKeyId ? `feature_${featureKeyId}` : '';
  return String(title)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100);
}

/** Scope as the mobile app infers it (Policy.fromMap) for docs without policyScope. */
export function inferPolicyScope(policy) {
  if (policy?.policyScope && POLICY_SCOPES.includes(policy.policyScope)) return policy.policyScope;
  if (policy?.featureKeyId) return 'feature_key';
  if (policy?.featureGroupId) return 'feature_group';
  return 'global';
}

// ── Validation ───────────────────────────────────────────────────────────────

function isIsoDate(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) && !Number.isNaN(Date.parse(value));
}

/**
 * Validate a publish request (SRS §7 PublishPolicyVersionRequest) against the
 * currently published version (null when nothing has been published yet).
 *
 * Returns { errors: string[], value } where `value` holds the normalised fields:
 * canonical markdown, trimmed strings and the integer version breakdown.
 */
export function validatePublishPayload(payload, currentVersion) {
  const errors = [];
  const body = payload && typeof payload === 'object' ? payload : {};

  const parsed = parseSemver(body.version);
  if (!parsed) {
    errors.push('version must be MAJOR.MINOR.PATCH, e.g. 1.2.0');
  } else {
    for (const [field, expected] of [
      ['majorVersion', parsed.major],
      ['minorVersion', parsed.minor],
      ['patchVersion', parsed.patch],
    ]) {
      if (body[field] !== undefined && body[field] !== expected) {
        errors.push(`${field} does not match version ${body.version}`);
      }
    }
    if (currentVersion && parseSemver(currentVersion) && compareSemver(parsed, currentVersion) <= 0) {
      errors.push(`version ${body.version} must be greater than the published version ${currentVersion}`);
    }
  }

  const title = typeof body.title === 'string' ? body.title.trim() : '';
  if (!title) errors.push('title is required');

  const contentMarkdown = canonicalizeMarkdown(typeof body.contentMarkdown === 'string' ? body.contentMarkdown : '');
  if (!contentMarkdown) errors.push('contentMarkdown is required');

  let summaryOfChanges = [];
  if (body.summaryOfChanges !== undefined && !Array.isArray(body.summaryOfChanges)) {
    errors.push('summaryOfChanges must be an array of strings');
  } else {
    summaryOfChanges = (body.summaryOfChanges ?? [])
      .filter(item => typeof item === 'string')
      .map(item => item.trim())
      .filter(Boolean);
  }
  const bump = parsed ? bumpTypeOf(currentVersion, body.version) : null;
  if (bump && bump !== 'patch' && summaryOfChanges.length === 0) {
    errors.push('summaryOfChanges needs at least one entry for an initial, major or minor version');
  }

  if (!isIsoDate(body.effectiveDate)) errors.push('effectiveDate must be an ISO 8601 date-time');

  const changelog = typeof body.changelog === 'string' && body.changelog.trim() ? body.changelog.trim() : null;

  return {
    errors,
    value: {
      version: parsed ? formatSemver(parsed) : '',
      majorVersion: parsed?.major ?? 0,
      minorVersion: parsed?.minor ?? 0,
      patchVersion: parsed?.patch ?? 0,
      bump,
      title,
      contentMarkdown,
      summaryOfChanges,
      changelog,
      effectiveDate: isIsoDate(body.effectiveDate) ? new Date(body.effectiveDate).toISOString() : '',
    },
  };
}

/**
 * Data-integrity problems for a published policy, worded for the admin UI.
 *
 * @param {object}   args
 * @param {object}   args.policy           master document
 * @param {object?}  args.version          version document for policy.currentVersion
 * @param {string?}  args.computedHash     policyContentHash(version.contentMarkdown)
 * @param {Set?}     args.featureGroupIds  existing featureGroups document IDs
 * @param {Set?}     args.featureKeyIds    existing featureKeys document IDs
 */
export function policyIntegrityIssues({ policy, version, computedHash, featureGroupIds, featureKeyIds }) {
  const issues = [];
  const parsed = parseSemver(policy.currentVersion);
  if (!parsed) {
    issues.push(`currentVersion "${policy.currentVersion ?? ''}" is not a valid version`);
  } else if (
    policy.currentMajorVersion !== parsed.major ||
    policy.currentMinorVersion !== parsed.minor ||
    policy.currentPatchVersion !== parsed.patch
  ) {
    issues.push('Version number fields do not match currentVersion');
  }

  if (!version) {
    issues.push(`Version document ${policy.currentVersion} is missing`);
  } else {
    if (version.contentHash !== policy.currentContentHash) {
      issues.push(`currentContentHash does not match version ${policy.currentVersion}`);
    }
    if (computedHash && version.contentHash !== computedHash) {
      issues.push(`Version ${policy.currentVersion} content does not match its stored hash`);
    }
  }

  const scope = inferPolicyScope(policy);
  if (scope === 'feature_group' && featureGroupIds && !featureGroupIds.has(policy.featureGroupId)) {
    issues.push(`Linked feature group "${policy.featureGroupId}" does not exist`);
  }
  if (scope === 'feature_key' && featureKeyIds && !featureKeyIds.has(policy.featureKeyId)) {
    issues.push(`Linked feature key "${policy.featureKeyId}" does not exist`);
  }
  return issues;
}
